import React, { memo, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { ActionButton } from '@fluentui/react/lib/Button'
import { Toggle } from '@fluentui/react/lib/Toggle'
import { ContainerPanel } from '../ContainerPanel'
import { PanelView } from '../PanelView'
import { ItemRecurrencePanelPart } from './ItemRecurrencePanelPart'
import { LocalizedDatePicker } from '../../pickers/LocalizedDatePicker'
import { useDidChangeDate } from '../../../hooks/dateHooks'
import { useTranslation } from 'react-i18next'
import { extenders } from '../../../style/teamsExtenders'
import { TextField } from '@fluentui/react/lib/TextField'
import { FlexRow } from '../../layout/FlexContainer'
import { reminderHelper } from '../../../common/src/helpers'
import { TimePickerCombo } from '../../timepicker/TimePickerCombo'

const StyledClearButton = styled(ActionButton)`
  height: auto;
`

const NotesTextField = extenders.textField(styled(TextField)`
  margin-bottom: 12px;
`)

const TimeContainer = styled(FlexRow)`
  align-items: center;
`
const ToggleContainer = styled.div``

const REMINDER_FREQUENCY_KEYS = [
  reminderHelper.FREQUENCY_TYPE.ONCE,
  reminderHelper.FREQUENCY_TYPE.DAILY,
  reminderHelper.FREQUENCY_TYPE.WEEKLY,
  reminderHelper.FREQUENCY_TYPE.BIWEEKLY,
  reminderHelper.FREQUENCY_TYPE.MONTHLY,
  reminderHelper.FREQUENCY_TYPE.YEARLY,
]

const ReminderPanelView = memo(
  ({
    reminderDate,
    allFollowers,
    onChangeReminderDate,
    onToggleAllFollowers,
    reminderFrequency,
    onChangeReminderFrequency,
    reminderNotes,
    onChangeReminderNotes,
    reminderEnabled,
    onChangeReminderEnabled,
    ...rest
  }) => {
    const onClearDate = useCallback(() => {
      onChangeReminderDate(0)
      onChangeReminderEnabled(false)
    }, [onChangeReminderDate, onChangeReminderEnabled])
    const { t } = useTranslation()

    const didChangeDateHandlerCreator = useDidChangeDate()

    const _handleChangeReminderDate = useCallback(
      value => {
        if (value) {
          onChangeReminderDate(value)
        } else {
          onClearDate()
        }
      },
      [onChangeReminderDate, onClearDate]
    )

    const handleChangeReminderDate = useMemo(() => {
      return didChangeDateHandlerCreator(reminderDate, _handleChangeReminderDate)
    }, [didChangeDateHandlerCreator, reminderDate, _handleChangeReminderDate])

    const onChangeNotes = useCallback(
      (ev, value) => {
        onChangeReminderNotes(value)
      },
      [onChangeReminderNotes]
    )

    const onChangeAllFollowers = useCallback(
      (ev, checked) => {
        onToggleAllFollowers(checked)
      },
      [onToggleAllFollowers]
    )

    const headerChildren = (
      <StyledClearButton disabled={!reminderDate && !reminderEnabled} onClick={onClearDate}>
        {t('general.clear')}
      </StyledClearButton>
    )
    return (
      <PanelView headerChildren={headerChildren} {...rest}>
        <LocalizedDatePicker
          label={t('item.reminder.reminder_date')}
          value={reminderEnabled ? reminderDate : null}
          onSelectDate={handleChangeReminderDate}
        />
        {reminderDate && reminderEnabled && (
          <TimeContainer>
            <TimePickerCombo id="startTimePicker" value={reminderDate} onChange={onChangeReminderDate} />
          </TimeContainer>
        )}
        {!!onToggleAllFollowers && reminderDate && reminderEnabled && (
          <ToggleContainer>
            <Toggle label={t('item.reminder.all_followers')} checked={allFollowers} onChange={onChangeAllFollowers} />
          </ToggleContainer>
        )}
        {reminderDate && reminderEnabled && (
          <ItemRecurrencePanelPart
            frequency={reminderFrequency}
            onChangeFrequency={onChangeReminderFrequency}
            frequencyKeys={REMINDER_FREQUENCY_KEYS}
            isReminder={true}
          />
        )}
        {reminderDate && reminderEnabled && (
          <NotesTextField
            id="itemReminder_notesTextField"
            validateOnFocusOut={true}
            validateOnLoad={false}
            validateOnFocusIn={false}
            onNotifyValidationResult={onChangeNotes}
            placeholder={t('item.reminder.notes_placeholder')}
            label={t('item.notes')}
            borderless={false}
            multiline
            resizable={false}
            defaultValue={reminderNotes}
            styles={{
              fieldGroup: {
                minHeight: '10em',
              },
            }}
          />
        )}
      </PanelView>
    )
  }
)

export const ItemReminderPanel = memo(
  ({
    reminderDate,
    headerText,
    allFollowers,
    onChangeReminderDate,
    onToggleAllFollowers,
    reminderFrequency,
    onChangeReminderFrequency,
    onDismiss,
    reminderNotes,
    onChangeReminderNotes,
    reminderEnabled,
    onChangeReminderEnabled,
    ...rest
  }) => {
    const { t } = useTranslation()
    const ht = headerText ?? t('item.reminder.edit_reminder')
    return (
      <ContainerPanel headerText={ht} onDismiss={onDismiss} {...rest}>
        <ReminderPanelView
          reminderDate={reminderDate}
          allFollowers={allFollowers}
          onChangeReminderDate={onChangeReminderDate}
          onToggleAllFollowers={onToggleAllFollowers}
          reminderFrequency={reminderFrequency}
          onChangeReminderFrequency={onChangeReminderFrequency}
          reminderNotes={reminderNotes}
          onChangeReminderNotes={onChangeReminderNotes}
          reminderEnabled={reminderEnabled}
          onChangeReminderEnabled={onChangeReminderEnabled}
        />
      </ContainerPanel>
    )
  }
)
